import React, { FC, useEffect, useState, useCallback } from 'react';
import { Alert, Button, Card, Col, Modal, Row } from "react-bootstrap";
import { FaExpandAlt } from "react-icons/fa";
import Loading from "../Loading";
import ObjectService from "../../http/ObjectService";
import {useAuth} from "../../store/AuthContext";

interface PropsModal {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    objectAnketaId: string | number;
    refreshTable: () => void;
}

const ObjectAnketaModalView: FC<PropsModal> = (props) => {
    const {user} = useAuth();
    const [objectAnketa, setObjectAnketa] = useState<any | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [needsRefresh, setNeedsRefresh] = useState<boolean>(false);
    const [fullscreen, setFullscreen] = useState<boolean>(false);

    const fetchAnketa = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await ObjectService.queryAnketa({
                includes: ["objects", "users", "photos"],
                options: {where: { id: props.objectAnketaId } }
            });

            if (data && data.length > 0) {
                const objectAnketaData = data[0];
                if (objectAnketaData.action === "DELETE") {
                    setError('Анкета объекта была удалена');
                    setNeedsRefresh(true);
                }
                setObjectAnketa(objectAnketaData);
            } else {
                setNeedsRefresh(true);
                setError("Анкета объекта не найдена");
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [props.objectAnketaId]);

    useEffect(() => {
        if (props.visible) {
            void fetchAnketa();
        }
    }, [props.visible, fetchAnketa]);

    const handleHideModal = () => {
        if (needsRefresh) props.refreshTable();
        props.setVisible(false);
    };

    return (
        <Modal show={props.visible} size={"xl"} fullscreen={fullscreen ? true : "md-down"} scrollable={true} onHide={handleHideModal}>
            <div className={"modal-header"}>
                <h5 className={"mb-0"}>Просмотр анкеты объекта</h5>
                <div className={"d-flex gap-3 ai-center"}>
                    <button
                        type={"button"}
                        className={"btn-link border-0 bg-transparent"}
                        onClick={() => setFullscreen(!fullscreen)}
                    >
                        <FaExpandAlt />
                    </button>
                    <button type={"button"} className={"btn-close"} onClick={handleHideModal}></button>
                </div>
            </div>

            {loading ? <div style={{height: "200px"}}><Loading/></div>
                :
                <>
                    {objectAnketa
                        ?
                        <Modal.Body>
                            {error && <Alert variant={"danger"} dismissible={true} onClose={() => setError(null)}>{error}</Alert>}

                            {objectAnketa.action !== "DELETE" && (
                                <>
                                    <Row className={"mb-3"}>
                                        <Col sm={"2"}>Объект</Col>
                                        <Col sm={"10"}>
                                            <Card className={"border-0 bg-light shadow-sm"}>
                                                <Card.Header>
                                                    {objectAnketa?.object?.name}
                                                </Card.Header>
                                                <Card.Body>
                                                    <Row>
                                                        <Col sm={"6"}>
                                                            <div className={"fs-10 text-muted"}>Категория</div>
                                                            <div className={"mb-2"}>{objectAnketa?.object?.category?.name || "—"}</div>
                                                            <div className={"fs-10 text-muted"}>Район</div>
                                                            <div>{objectAnketa?.object?.district || "—"}</div>
                                                        </Col>
                                                        <Col sm={"6"}>
                                                            <div className={"fs-10 text-muted"}>Адрес</div>
                                                            <div className={"mb-2"}>{objectAnketa?.object?.address || "—"}</div>
                                                            <div className={"fs-10 text-muted"}>Комментарий</div>
                                                            <div>{objectAnketa?.object?.comment || "—"}</div>
                                                        </Col>
                                                    </Row>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                    <Row className={"mb-3"}>
                                        <Col sm={"2"}>Фамилия</Col>
                                        <Col sm={"10"}>{objectAnketa.lastname}</Col>
                                    </Row>
                                    <Row className={"mb-3"}>
                                        <Col sm={"2"}>Имя</Col>
                                        <Col sm={"10"}>{objectAnketa.firstname}</Col>
                                    </Row>
                                    <Row className={"mb-3"}>
                                        <Col sm={"2"}>Отчество</Col>
                                        <Col sm={"10"}>
                                            {objectAnketa.middlename?.length > 0 ? (
                                                objectAnketa.middlename
                                            ) : (
                                                <i className={"text-muted fs-11"}>Не указано</i>
                                            )}
                                        </Col>
                                    </Row>
                                    <Row className={"mb-3"}>
                                        <Col sm={"2"}>Должность</Col>
                                        <Col sm={"10"}>{objectAnketa.position || "—"}</Col>
                                    </Row>
                                    <Row className={"mb-3"}>
                                        <Col sm={"2"}>E-mail</Col>
                                        <Col sm={"10"}>{objectAnketa.email}</Col>
                                    </Row>
                                    <Row className={"mb-3"}>
                                        <Col sm={"2"}>Телефон</Col>
                                        <Col sm={"10"}>{objectAnketa.phone}</Col>
                                    </Row>
                                    <Row className={"mb-3"}>
                                        <Col sm={"2"}>Дата рождения</Col>
                                        <Col sm={"10"}>
                                            {objectAnketa.birthDate ? (
                                                new Date(objectAnketa.birthDate).toLocaleDateString()
                                            ) : (
                                                <i className={"text-muted fs-11"}>Не указана</i>
                                            )}
                                        </Col>
                                    </Row>
                                    <Row className={"mb-3"}>
                                        <Col sm={"2"}>Фотографии</Col>
                                        <Col sm={"10"}>
                                            {objectAnketa.photos && objectAnketa.photos.length > 0
                                                ?
                                                <div className={"d-flex gap-3"}>
                                                    {objectAnketa.photos && objectAnketa.photos.map((photo: any) => {
                                                        const srcUrl = import.meta.env.MODE === 'development' ? import.meta.env.VITE_SERVER_URL_DEV :
                                                            "/";

                                                        return (
                                                            <img key={photo.id} alt={""} height={100} src={`${srcUrl}${photo.path}`}/>
                                                        )
                                                    })}
                                                </div>
                                                :
                                                <i className={"text-muted fs-11"}>Не загружены</i>
                                            }
                                        </Col>
                                    </Row>
                                    <Row className={"mb-3"}>
                                        <Col sm={"2"}>Комментарий</Col>
                                        <Col sm={"10"}>
                                            {objectAnketa.comment.length > 0
                                                ?
                                                objectAnketa.comment :
                                                <i className={"text-muted fs-11"}>Не указан</i>
                                            }
                                        </Col>
                                    </Row>
                                    <Row className={"mb-3"}>
                                        <Col sm={"2"}>Создан</Col>
                                        <Col sm={"10"}>{new Date(objectAnketa.createdAt).toLocaleString()}</Col>
                                    </Row>

                                    {user.role === "ADMIN" && (
                                        <Row className={"mb-3"}>
                                            <Col sm={"2"}>Кем создан</Col>
                                            <Col sm={"10"}>
                                                {(() => {
                                                    if (objectAnketa.user) {
                                                        if (objectAnketa.user.role === "ADMIN") {
                                                            return (<div>Администратор ({objectAnketa.user.email})</div>);
                                                        }
                                                        if (objectAnketa.user.role === "USER") {
                                                            return (<div>Пользователь ({objectAnketa.user.email})</div>);
                                                        }
                                                    }
                                                    return null;
                                                })()}
                                            </Col>
                                        </Row>
                                    )}
                                </>
                            )}
                        </Modal.Body>
                        :
                        <Modal.Body>
                            <Alert variant={"warning"}>{error || "Данные анкеты недоступны"}</Alert>
                        </Modal.Body>
                    }
                    <Modal.Footer>
                        <Button variant={"secondary"} onClick={handleHideModal}>Закрыть</Button>
                    </Modal.Footer>
                </>
            }
        </Modal>
    );
};

export default ObjectAnketaModalView;
